import React, {useEffect, useMemo, useState} from "react";
import {
    Banknote, Check, CheckCircle2, CreditCard, Delete, FileText, Loader, Printer, Search, Trash2, UserX, Wallet, X,
} from "lucide-react";
import {discountRefusal, isPercentDiscount, round2, settlePayments} from "./posMath";
import {newId} from "./posApi";
import {Thumb} from "./PosProductGrid";

// Fenêtres de la caisse web — dessin des feuilles de la caisse desktop
// (electron-pos AppleUI.jsx) : fond assombri et flouté, feuille arrondie à
// 26 px, en-tête translucide avec ✕ rond, contenu qui défile SEUL entre un
// en-tête et un pied fixes, page derrière bloquée.

export const formatMoney = (value) =>
    `${(Number(value) || 0).toLocaleString("fr-FR", {minimumFractionDigits: 2, maximumFractionDigits: 2})} DH`;

export const money = (v) => (Number(v) || 0).toLocaleString("fr-FR", {minimumFractionDigits: 2, maximumFractionDigits: 2});

export const discountLabel = (d) =>
    isPercentDiscount(d.type) ? `${Number(d.value) || 0} %` : formatMoney(d.value);

// ── Socle commun ────────────────────────────────────────────────────────────

const EASE = "ease-[cubic-bezier(0.32,0.72,0,1)]";
const SHEET_FRAME = "ring-1 ring-black/[0.08] dark:ring-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.28),0_8px_24px_rgba(0,0,0,0.12)]";
const overlayClass = (mounted, side = false) =>
    `fixed inset-0 z-50 flex transition-[background-color,backdrop-filter] duration-300 ${
        mounted ? "bg-black/30 backdrop-blur-[6px]" : "bg-black/0 backdrop-blur-0"} ${
        side ? "justify-end items-stretch p-3" : "items-end sm:items-center justify-center sm:p-4"}`;
const sheetMotion = (mounted, side = false) =>
    `transition-[transform,opacity] duration-[420ms] ${EASE} ${
        side
            ? (mounted ? "translate-x-0" : "translate-x-[calc(100%+24px)]")
            : (mounted ? "translate-y-0 opacity-100 sm:scale-100" : "translate-y-8 opacity-0 sm:translate-y-2 sm:scale-95")}`;

// Bloque le défilement de la page tant qu'au moins une fenêtre est ouverte.
let openSheets = 0;
const useSheetMount = () => {
    const [mounted, setMounted] = useState(false);
    useEffect(() => {
        const frame = requestAnimationFrame(() => setMounted(true));
        if (openSheets++ === 0) document.body.style.overflow = "hidden";
        return () => {
            cancelAnimationFrame(frame);
            if (--openSheets === 0) document.body.style.overflow = "";
        };
    }, []);
    return mounted;
};

const SIZES = {sm: "sm:max-w-[400px]", md: "sm:max-w-[480px]", lg: "sm:max-w-[620px]"};

export const Sheet = ({title, subtitle, onClose, children, footer, size = "md", side = false, busy = false,
                          bodyClass = "p-4", tone = "bg-white dark:bg-gray-800"}) => {
    const mounted = useSheetMount();
    const close = () => !busy && onClose();
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose, busy]);

    return (
        <div className={overlayClass(mounted, side)} onClick={close}>
            <div role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}
                 className={`${tone} ${SHEET_FRAME} ${sheetMotion(mounted, side)} flex flex-col overflow-hidden ${
                     side
                         ? "w-[380px] max-w-[92vw] h-full rounded-[26px]"
                         : `w-full ${SIZES[size]} max-h-[92dvh] rounded-t-[26px] sm:rounded-[26px]`}`}>
                <div className="flex items-center gap-2.5 pl-5 pr-3.5 py-3 flex-shrink-0 border-b border-black/[0.08] dark:border-white/10 bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl backdrop-saturate-150">
                    <div className="flex-1 min-w-0">
                        <h3 className="text-[17px] font-semibold tracking-[-0.2px] text-gray-900 dark:text-white truncate">{title}</h3>
                        {subtitle && <p className="text-[13px] text-gray-500 dark:text-gray-400 truncate">{subtitle}</p>}
                    </div>
                    <button type="button" onClick={close} aria-label="Fermer" disabled={busy}
                            className="w-[30px] h-[30px] flex-shrink-0 rounded-full flex items-center justify-center bg-[rgba(120,120,128,0.12)] hover:bg-[rgba(120,120,128,0.2)] text-gray-500 dark:text-gray-300 disabled:opacity-50">
                        <X className="w-3.5 h-3.5" strokeWidth={3}/>
                    </button>
                </div>
                <div className={`flex-1 min-h-0 overflow-y-auto overscroll-contain ${bodyClass}`}>{children}</div>
                {footer && (
                    <div className="flex gap-2 px-4 py-3.5 flex-shrink-0 border-t border-black/[0.08] dark:border-white/10 bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
};

const BTN = {
    primary: "bg-primary text-white hover:bg-primary-dark shadow-[0_6px_16px_rgba(77,182,172,0.3)]",
    secondary: "bg-[rgba(120,120,128,0.12)] text-gray-900 dark:text-white hover:bg-[rgba(120,120,128,0.2)]",
    tinted: "bg-primary/10 text-primary hover:bg-primary/20",
    destructive: "bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20",
    destructiveSolid: "bg-red-500 text-white hover:bg-red-600 shadow-[0_6px_16px_rgba(239,68,68,0.3)]",
};
export const SheetButton = ({variant = "primary", loading = false, disabled, children, className = "", ...props}) => (
    <button type="button" disabled={disabled || loading} {...props}
            className={`flex-1 min-h-[48px] px-4 rounded-[14px] text-[15px] font-semibold flex items-center justify-center gap-2 transition active:scale-[0.98] disabled:opacity-50 disabled:shadow-none disabled:cursor-not-allowed ${BTN[variant]} ${className}`}>
        {loading && <Loader className="w-4 h-4 animate-spin"/>}
        {children}
    </button>
);

export const SectionLabel = ({children}) => (
    <p className="mx-1 mb-1.5 text-xs font-medium uppercase tracking-[0.3px] text-gray-500 dark:text-gray-400">{children}</p>
);
export const Group = ({children, className = ""}) => (
    <div className={`rounded-2xl bg-white dark:bg-gray-800 overflow-hidden ring-1 ring-black/[0.06] dark:ring-white/10 ${className}`}>{children}</div>
);
export const fieldClass = "w-full px-3.5 py-[11px] rounded-xl border-0 bg-[rgba(120,120,128,0.12)] text-[15px] text-gray-900 dark:text-white placeholder:text-gray-400 outline-none focus:ring-[3px] focus:ring-primary/35 transition-shadow";
export const bigAmountClass = `${fieldClass} text-center text-3xl font-semibold tabular-nums py-4`;

// ── Client (panneau latéral) ────────────────────────────────────────────────

export const ClientPickerModal = ({clients, selectedId, onSelect, onClear, onClose}) => {
    const [query, setQuery] = useState("");
    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        const list = q
            ? clients.filter((c) =>
                c.name.toLowerCase().includes(q) || c.phone.includes(q) || c.email.toLowerCase().includes(q))
            : clients;
        return list.slice(0, 80);
    }, [clients, query]);

    return (
        <Sheet title="Choisir un client" subtitle={`${clients.length} client${clients.length > 1 ? "s" : ""}`}
               onClose={onClose} side bodyClass="px-2.5 pb-2.5">
            <div className="sticky top-0 z-[1] -mx-2.5 px-4 pt-3 pb-2 bg-white/85 dark:bg-gray-800/85 backdrop-blur-xl">
                <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                    <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
                           placeholder="Nom, téléphone ou email" className={`${fieldClass} pl-9`}/>
                </div>
            </div>
            <ul className="space-y-0.5 pt-1">
                {selectedId && (
                    <li>
                        <button type="button" onClick={onClear}
                                className="w-full flex items-center gap-2.5 px-3 py-3 mb-1 rounded-[14px] text-left text-[15px] font-medium text-red-600 dark:text-red-400 bg-red-500/[0.08] hover:bg-red-500/15">
                            <UserX className="w-4 h-4"/> Retirer le client de la vente
                        </button>
                    </li>
                )}
                {results.map((c) => {
                    const active = c.id === selectedId;
                    return (
                        <li key={c.id}>
                            <button type="button" onClick={() => onSelect(c)}
                                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-[14px] text-left transition-colors ${
                                        active ? "bg-primary/10" : "hover:bg-[rgba(120,120,128,0.10)]"}`}>
                                <span className="w-9 h-9 flex-shrink-0 rounded-full bg-primary/[0.12] text-primary text-[13px] font-bold flex items-center justify-center">
                                    {c.name.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                                </span>
                                <span className="flex-1 min-w-0">
                                    <span className="block text-[15px] font-medium text-gray-900 dark:text-white truncate">{c.name}</span>
                                    <span className="block text-[13px] text-gray-500 dark:text-gray-400 truncate">{c.phone || c.email || "—"}</span>
                                </span>
                                {c.clientTypeName && (
                                    <span className="px-1.5 py-0.5 rounded-md bg-primary/10 text-primary text-[10px] font-bold uppercase">{c.clientTypeName}</span>
                                )}
                                {active && <Check className="w-4 h-4 text-primary flex-shrink-0" strokeWidth={2.5}/>}
                            </button>
                        </li>
                    );
                })}
                {results.length === 0 && (
                    <li className="px-4 py-10 text-center text-sm text-gray-400">Aucun client trouvé</li>
                )}
            </ul>
        </Sheet>
    );
};

// ── Remise ──────────────────────────────────────────────────────────────────

// target : une ligne du panier, ou null = tout le ticket.
export const DiscountModal = ({discounts, target, lines, clientTypeId, onApply, onClearAll, onClose}) => {
    const scopeLines = target ? [target] : lines;
    const options = discounts.map((d) => {
        // Sur tout le ticket : applicable si au moins une ligne l'accepte.
        const refusals = scopeLines.map((l) => discountRefusal(d, {clientTypeId, product: l.product}));
        const okCount = refusals.filter((r) => !r).length;
        return {discount: d, refusal: okCount === 0 ? refusals[0] : null, partial: okCount > 0 && okCount < scopeLines.length};
    }).sort((a, b) => (a.refusal ? 1 : 0) - (b.refusal ? 1 : 0));
    const hasDiscounts = scopeLines.some((l) => (l.discounts || []).length > 0);

    return (
        <Sheet title="Remise" subtitle={target ? target.name : "Sur tout le ticket"} onClose={onClose} size="md"
               tone="bg-[#F2F2F7] dark:bg-gray-900"
               footer={hasDiscounts && (
                   <SheetButton variant="destructive" onClick={onClearAll}>
                       <Trash2 className="w-4 h-4"/>
                       {target ? "Retirer la remise de la ligne" : "Retirer toutes les remises"}
                   </SheetButton>
               )}>
            <SectionLabel>Remises prédéfinies</SectionLabel>
            <Group>
                {options.map(({discount, refusal, partial}, i) => (
                    <button key={discount.id} type="button" disabled={!!refusal} onClick={() => onApply(discount)}
                            className={`w-full min-h-[52px] flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[rgba(120,120,128,0.08)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent ${
                                i ? "border-t border-black/[0.06] dark:border-white/10" : ""}`}>
                        <span className="flex-1 min-w-0">
                            <span className="block text-base text-gray-900 dark:text-white truncate">{discount.name}</span>
                            {(refusal || partial) && (
                                <span className="block text-xs text-orange-600 dark:text-orange-400">
                                    {refusal || "Appliquée seulement aux produits éligibles"}
                                </span>
                            )}
                        </span>
                        <span className="px-2.5 py-1 rounded-full bg-red-500/10 text-red-600 dark:text-red-400 text-sm font-semibold tabular-nums whitespace-nowrap">
                            −{discountLabel(discount)}
                        </span>
                    </button>
                ))}
                {options.length === 0 && (
                    <p className="px-5 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                        Aucune remise active. Créez-en dans Catalogue › Remises.
                    </p>
                )}
            </Group>
        </Sheet>
    );
};

// ── Variante ────────────────────────────────────────────────────────────────

export const VariantModal = ({product, onPick, onClose}) => (
    <Sheet title="Choisir une variante" subtitle={product.name} onClose={onClose} size="lg" bodyClass="p-3">
        <div className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] border-t border-l border-gray-300/60 dark:border-gray-600/60 rounded-xl overflow-hidden">
            {product.variants.map((v) => (
                <button key={v.id} type="button" onClick={() => onPick(v)}
                        className="flex flex-col text-center bg-white dark:bg-gray-800 border-r border-b border-gray-300/60 dark:border-gray-600/60 hover:shadow-[inset_0_0_0_2px_#4DB6AC] active:bg-primary/10">
                    <Thumb image={product.image} color={product.color} label={v.name} className="w-full h-[58px]" initialsClass="text-[17px]"/>
                    <span className="mx-1.5 my-1.5 text-[13px] font-semibold leading-tight text-gray-900 dark:text-white line-clamp-2 min-h-[32px]">{v.name}</span>
                    <span className="mt-auto py-1 bg-primary/10 text-primary text-sm font-extrabold tabular-nums">
                        {(Number(v.price) || 0).toFixed(2)} <span className="text-[10px] font-bold">DH</span>
                    </span>
                </button>
            ))}
        </div>
    </Sheet>
);

// ── Encaissement ────────────────────────────────────────────────────────────
// Dessin de la caisse desktop (electron-pos PaymentModal.jsx) : à gauche le
// montant saisi, les billets rapides, le pavé numérique et les modes de
// paiement ; à droite le total, la progression et les paiements ajoutés.
// Saisir un montant puis toucher un mode l'ajoute (sans montant = le reste).

export const KEYS = ["1", "2", "3", "C", "4", "5", "6", "⌫", "7", "8", "9", ".", "00", "0"];

export const applyKey = (prev, key) => {
    if (key === "⌫") return prev.slice(0, -1);
    if (key === "C") return "";
    if (key === ".") return prev.includes(".") ? prev : (prev === "" ? "0." : `${prev}.`);
    if (prev === "0") return key === "00" ? prev : key;
    const dot = prev.indexOf(".");
    if (dot !== -1 && prev.length - dot > 2) return prev;
    if (prev.length >= 9) return prev;
    return prev + key;
};

// Billets / arrondis juste au-dessus du reste dû.
const quickAmountsFor = (remaining) => {
    if (!(remaining > 0)) return [];
    const out = [];
    [10, 50, 100].forEach((step) => {
        const v = Math.ceil(remaining / step) * step;
        if (v > remaining) out.push(v);
    });
    [20, 50, 100, 200, 500].forEach((b) => { if (b > remaining) out.push(b); });
    return [...new Set(out)].sort((a, b) => a - b).slice(0, 4);
};

const methodIcon = (method) => {
    const s = `${method.icon || ""} ${method.name || ""}`.toLowerCase();
    if (/cash|esp[eè]ce|liquide|monnaie/.test(s)) return Banknote;
    if (/card|carte|cb|tpe|visa|credit/.test(s)) return CreditCard;
    if (/ch[eè]que|check/.test(s)) return FileText;
    return Wallet;
};

const card = "bg-white dark:bg-gray-800 rounded-2xl shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_16px_rgba(0,0,0,0.04)] ring-1 ring-black/[0.06] dark:ring-white/10";

export const PaymentModal = ({total, methods, saving, onConfirm, onClose}) => {
    const [rows, setRows] = useState([]);
    const [input, setInput] = useState("");
    const {paid, change, remaining} = settlePayments(rows, total);
    const fullyPaid = remaining === 0 && (rows.length > 0 || round2(total) === 0);
    const quick = useMemo(() => quickAmountsFor(remaining), [remaining]);
    const nameOf = (paymentId) => methods.find((m) => m.id === paymentId)?.name || "Paiement";
    const mounted = useSheetMount();

    // Clavier physique : chiffres, point/virgule, retour arrière, Entrée = valider.
    useEffect(() => {
        const handler = (e) => {
            if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target?.tagName)) return;
            let key = null;
            if (/^[0-9]$/.test(e.key)) key = e.key;
            else if (e.key === "." || e.key === ",") key = ".";
            else if (e.key === "Backspace") key = "⌫";
            else if (e.key === "Delete") key = "C";
            else if (e.key === "Escape") { if (!saving) onClose(); return; }
            if (!key) return;
            e.preventDefault();
            setInput((prev) => applyKey(prev, key));
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onClose, saving]);

    const addPayment = (method) => {
        const typed = Number(input);
        const amount = round2(input !== "" && typed > 0 ? typed : remaining);
        if (!(amount > 0)) return;
        setRows((prev) => [...prev, {id: newId(), paymentId: method.id, amount}]);
        setInput("");
    };

    const validate = () => {
        const settled = settlePayments(rows, total);
        onConfirm(settled.payments, settled.change);
    };

    const pct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 100;

    return (
        <div className={overlayClass(mounted)} onClick={() => !saving && onClose()}>
            <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Paiement"
                 className={`w-full max-w-[920px] max-h-[94dvh] flex flex-col overflow-hidden rounded-t-[26px] sm:rounded-[26px] bg-[#F2F2F7] dark:bg-gray-900 ${SHEET_FRAME} ${sheetMotion(mounted)}`}>
                {/* Barre de navigation : Fermer · Paiement · total */}
                <div className="flex items-center px-5 py-2.5 border-b border-black/[0.08] dark:border-white/10 bg-white/70 dark:bg-gray-800/70 backdrop-blur flex-shrink-0">
                    <button type="button" onClick={onClose} disabled={saving}
                            className="min-w-[90px] text-left text-[17px] text-primary disabled:opacity-50">Fermer</button>
                    <div className="flex-1 text-center text-[17px] font-semibold text-gray-900 dark:text-white">Paiement</div>
                    <div className="min-w-[90px] text-right text-[15px] text-gray-500 tabular-nums">{money(total)} DH</div>
                </div>

                <div className="flex-1 flex flex-col md:flex-row gap-4 p-4 sm:p-5 min-h-0 overflow-y-auto overscroll-contain">
                    {/* Gauche : montant + pavé + modes */}
                    <div className="flex-1 flex flex-col gap-3.5 min-w-0">
                        <div className={`${card} px-5 py-3.5 text-center`}>
                            <div className={`text-5xl font-semibold tracking-[-1px] leading-tight tabular-nums ${input ? "text-gray-900 dark:text-white" : "text-gray-400"}`}>
                                {input || money(remaining)}
                                <span className="ml-2 text-[22px] font-medium text-gray-400">DH</span>
                            </div>
                            <div className={`inline-flex items-center gap-1.5 mt-2.5 px-3 py-1 rounded-[14px] text-sm ${
                                fullyPaid ? "bg-green-500/10 text-green-600" : "bg-orange-500/10 text-orange-600"}`}>
                                {fullyPaid && <CheckCircle2 className="w-4 h-4"/>}
                                Reste à payer <span className="font-bold tabular-nums">{money(remaining)} DH</span>
                            </div>
                        </div>

                        {quick.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                <button type="button" onClick={() => setInput(String(remaining))}
                                        className="px-4 py-2 rounded-full text-sm font-semibold bg-primary text-white">Montant exact</button>
                                {quick.map((v) => (
                                    <button key={v} type="button" onClick={() => setInput(String(v))}
                                            className="px-4 py-2 rounded-full text-sm font-semibold bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 ring-1 ring-black/[0.06] dark:ring-white/10 tabular-nums">
                                        {v} DH
                                    </button>
                                ))}
                            </div>
                        )}

                        <div className="grid grid-cols-4 gap-2">
                            {KEYS.map((k) => {
                                const isClear = k === "C";
                                const isFn = k === "⌫" || k === ".";
                                return (
                                    <button key={k} type="button" onClick={() => setInput((p) => applyKey(p, k))}
                                            aria-label={k === "⌫" ? "Effacer le dernier chiffre" : isClear ? "Tout effacer" : k}
                                            className={`${k === "00" || k === "0" ? "col-span-2" : ""} h-[54px] rounded-[14px] flex items-center justify-center tabular-nums transition-colors select-none ${
                                                isClear ? "bg-red-500/10 text-red-600 text-xl font-semibold active:bg-red-500/20"
                                                    : isFn ? "bg-primary/[0.12] text-primary text-[26px] active:bg-primary/25"
                                                        : "bg-[#E9E9EB] dark:bg-gray-700 text-gray-900 dark:text-white text-[26px] active:bg-[#D1D1D6] dark:active:bg-gray-600"}`}>
                                        {k === "⌫" ? <Delete className="w-6 h-6"/> : k}
                                    </button>
                                );
                            })}
                        </div>

                        <div>
                            <p className="mx-1 mb-1.5 text-[13px] uppercase tracking-[0.2px] text-gray-500">Modes de paiement</p>
                            <div className="grid grid-cols-[repeat(auto-fill,minmax(128px,1fr))] gap-2.5">
                                {methods.map((m) => {
                                    const Icon = methodIcon(m);
                                    return (
                                        <button key={m.id} type="button" onClick={() => addPayment(m)} disabled={remaining <= 0 || saving}
                                                className={`${card} h-[92px] px-2 flex flex-col items-center justify-center gap-2 text-gray-900 dark:text-white disabled:opacity-40 hover:ring-primary/60 active:scale-[0.98] transition`}>
                                            <span className="w-10 h-10 rounded-full bg-primary/[0.12] text-primary flex items-center justify-center">
                                                <Icon className="w-5 h-5"/>
                                            </span>
                                            <span className="text-sm font-medium leading-tight text-center line-clamp-2">{m.name}</span>
                                        </button>
                                    );
                                })}
                                {methods.length === 0 && (
                                    <p className="col-span-full p-3 text-sm text-center text-gray-500">Aucun mode de paiement actif (Paramètres › Paiements).</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Droite : total + paiements */}
                    <div className="md:w-[320px] flex flex-col gap-3.5 min-w-0">
                        <div className={`${card} px-4 py-3.5`}>
                            <p className="text-[13px] text-gray-500">Total à payer</p>
                            <p className="text-[32px] font-bold tracking-[-0.5px] tabular-nums text-gray-900 dark:text-white">
                                {money(total)} <span className="text-[17px] font-medium text-gray-400">DH</span>
                            </p>
                            {total > 0 && (
                                <div className="mt-2.5">
                                    <div className="h-1.5 rounded-full bg-[#E9E9EB] dark:bg-gray-700 overflow-hidden">
                                        <div className={`h-full rounded-full transition-[width] duration-300 ${fullyPaid ? "bg-green-500" : "bg-primary"}`}
                                             style={{width: `${pct}%`}}/>
                                    </div>
                                    <div className="flex justify-between mt-1.5 text-xs text-gray-500 tabular-nums">
                                        <span>Encaissé {money(paid)} DH</span><span>{pct} %</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className={`${card} flex-1 min-h-[120px] overflow-y-auto`}>
                            {rows.length === 0 ? (
                                <p className="p-6 text-center text-sm text-gray-500 leading-relaxed">
                                    Aucun paiement ajouté.<br/>Saisissez un montant puis choisissez un mode.
                                </p>
                            ) : rows.map((r, i) => (
                                <div key={r.id} className={`flex items-center gap-3 px-4 py-3 ${i ? "border-t border-black/[0.06] dark:border-white/10" : ""}`}>
                                    <span className="flex-1 text-[15px] text-gray-900 dark:text-white truncate">{nameOf(r.paymentId)}</span>
                                    <span className="text-[15px] font-semibold tabular-nums text-gray-900 dark:text-white">{money(r.amount)} DH</span>
                                    <button type="button" onClick={() => setRows((prev) => prev.filter((x) => x.id !== r.id))}
                                            aria-label="Retirer" className="w-7 h-7 rounded-full flex items-center justify-center text-red-500 hover:bg-red-500/10">
                                        <Trash2 className="w-4 h-4"/>
                                    </button>
                                </div>
                            ))}
                        </div>

                        {change > 0 && (
                            <div className="rounded-2xl bg-green-500/10 px-4 py-3 flex justify-between items-baseline text-green-700 dark:text-green-400">
                                <span className="text-sm font-medium">Monnaie à rendre</span>
                                <span className="text-2xl font-bold tabular-nums">{money(change)} DH</span>
                            </div>
                        )}

                        <button type="button" onClick={validate} disabled={!fullyPaid || saving}
                                className={`py-[15px] rounded-[14px] text-white text-[17px] font-semibold flex items-center justify-center gap-2 transition disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:shadow-none disabled:cursor-not-allowed ${
                                    fullyPaid ? "bg-green-500 hover:bg-green-600 shadow-[0_6px_16px_rgba(52,199,89,0.35)]" : "bg-primary"}`}>
                            {saving ? <Loader className="w-5 h-5 animate-spin"/> : <Check className="w-5 h-5" strokeWidth={2.5}/>}
                            Valider la vente
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const SaleDoneModal = ({total, change, invoiceLoading, onInvoice, onPrint, onNewSale}) => (
    <Sheet title="Vente encaissée" onClose={onNewSale} size="sm"
           footer={<div className="flex-1 flex flex-col gap-2">
               <div className="flex gap-2">
                   <SheetButton variant="secondary" onClick={onPrint}>
                       <Printer className="w-4 h-4"/> Ticket
                   </SheetButton>
                   <SheetButton variant="secondary" loading={invoiceLoading} onClick={onInvoice}>
                       {!invoiceLoading && <FileText className="w-4 h-4"/>} Facture PDF
                   </SheetButton>
               </div>
               <SheetButton variant="primary" onClick={onNewSale} autoFocus>Nouvelle vente</SheetButton>
           </div>}>
        <div className="flex flex-col items-center text-center py-4">
            <span className="w-16 h-16 rounded-full bg-green-500/[0.12] flex items-center justify-center">
                <CheckCircle2 className="w-9 h-9 text-green-500"/>
            </span>
            <p className="mt-4 text-[40px] leading-none font-semibold tracking-[-0.5px] tabular-nums text-gray-900 dark:text-white">
                {money(total)} <span className="text-xl font-medium text-gray-400">DH</span>
            </p>
            {change > 0 && (
                <p className="mt-4 px-4 py-2 rounded-2xl bg-green-500/10 text-green-700 dark:text-green-400">
                    Monnaie à rendre <span className="ml-1 text-lg font-bold tabular-nums">{money(change)} DH</span>
                </p>
            )}
            <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                La vente apparaît dans Activité et dans le journal de la caisse web.
            </p>
        </div>
    </Sheet>
);

// ── Session de caisse ───────────────────────────────────────────────────────

export const JournalOpenModal = ({employees, employeeId, onEmployeeChange, saving, onConfirm, onClose}) => {
    const [amount, setAmount] = useState("");
    return (
        <Sheet title="Ouvrir la caisse web" onClose={onClose} size="sm" busy={saving}
               tone="bg-[#F2F2F7] dark:bg-gray-900" bodyClass="p-4 space-y-4"
               footer={<>
                   <SheetButton variant="secondary" onClick={onClose} disabled={saving}>Annuler</SheetButton>
                   <SheetButton variant="primary" loading={saving} disabled={!employeeId}
                                onClick={() => onConfirm(Number(amount) || 0)}>Ouvrir la caisse</SheetButton>
               </>}>
            <label className="block">
                <SectionLabel>Employé</SectionLabel>
                <select value={employeeId || ""} onChange={(e) => onEmployeeChange(e.target.value)} className={fieldClass}>
                    <option value="" disabled>Choisir…</option>
                    {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
            </label>
            <label className="block">
                <SectionLabel>Fond de caisse (DH)</SectionLabel>
                <input type="number" min="0" step="0.01" inputMode="decimal" autoFocus value={amount}
                       onChange={(e) => setAmount(e.target.value)} placeholder="0,00" className={bigAmountClass}/>
            </label>
        </Sheet>
    );
};

export const JournalCloseModal = ({journal, salesTotal, movementsTotal = 0, saving, onConfirm, onClose}) => {
    const [amount, setAmount] = useState("");
    const counted = Number(amount) || 0;
    const expected = round2((journal?.startTotal || 0) + (salesTotal || 0) + (movementsTotal || 0));
    const rows = [
        ["Fond de caisse", journal?.startTotal],
        ["Ventes de la session (navigateur)", salesTotal],
        ...(movementsTotal ? [["Entrées / sorties d'espèces", movementsTotal]] : []),
        ["Attendu", expected],
    ];
    const gap = round2(counted - expected);
    return (
        <Sheet title="Clôturer la caisse web" onClose={onClose} size="sm" busy={saving}
               tone="bg-[#F2F2F7] dark:bg-gray-900" bodyClass="p-4 space-y-4"
               footer={<>
                   <SheetButton variant="secondary" onClick={onClose} disabled={saving}>Annuler</SheetButton>
                   <SheetButton variant="destructiveSolid" loading={saving} disabled={amount === ""}
                                onClick={() => onConfirm(counted)}>Clôturer</SheetButton>
               </>}>
            <div>
                <SectionLabel>Session</SectionLabel>
                <Group>
                    {rows.map(([label, value], i) => {
                        const last = i === rows.length - 1;
                        return (
                            <div key={label} className={`flex justify-between items-baseline px-4 py-3 ${i ? "border-t border-black/[0.06] dark:border-white/10" : ""}`}>
                                <span className={`text-[15px] ${last ? "font-semibold text-gray-900 dark:text-white" : "text-gray-600 dark:text-gray-300"}`}>{label}</span>
                                <span className={`tabular-nums ${last ? "text-lg font-bold text-gray-900 dark:text-white" : "text-[15px] font-medium text-gray-900 dark:text-white"}`}>
                                    {formatMoney(value)}
                                </span>
                            </div>
                        );
                    })}
                </Group>
            </div>
            <label className="block">
                <SectionLabel>Montant compté en caisse (DH)</SectionLabel>
                <input type="number" min="0" step="0.01" inputMode="decimal" autoFocus value={amount}
                       onChange={(e) => setAmount(e.target.value)} placeholder="0,00" className={bigAmountClass}/>
            </label>
            {amount !== "" && (
                <p className={`text-center px-3 py-2 rounded-xl text-sm font-medium ${
                    gap === 0 ? "bg-green-500/10 text-green-700 dark:text-green-400" : "bg-orange-500/10 text-orange-700 dark:text-orange-400"}`}>
                    Écart estimé : <span className="font-bold tabular-nums">{formatMoney(gap)}</span>
                    <span className="block text-xs font-normal opacity-80">tous modes de paiement confondus</span>
                </p>
            )}
        </Sheet>
    );
};
